import Warning from "@mui/icons-material/Warning";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import { AppDrawer } from "@thalamos/frontend";

import { useServerVersion } from "../api/axios/serverVersion.js";
import { config } from "../config/index.js";
import { useUser } from "../Context/UserContext.js";
import {
  useListWaitingWork,
  waitingShownBy,
  WaitingWorkContext,
} from "../Context/WaitingWorkContext.js";
import {
  SessionContextNavigation,
  useSessionContextNavItems,
} from "./SessionContextNavigation.js";

/** The shell every authenticated page renders inside. */
export const MainLayout = () => {
  const { t } = useTranslation();
  const { palette, breakpoints } = useTheme();
  const serverVersion = useServerVersion();
  const { user } = useUser();
  const { current } = useSessionContextNavItems();
  // A phone has no room for the product's name beside the menu button, so it
  // names the section being looked at instead.
  const isPhone = useMediaQuery(breakpoints.down("sm"));
  const sessionContext = user?.sessionContext ?? null;

  // Read here rather than in the navigation, which stays a rendering of what
  // it is given, and shared with the page so it does not read the same again.
  const waitingWork = useListWaitingWork();
  const waitingTotal = waitingShownBy(waitingWork.listing)?.groups.reduce(
    (sum, slice) => sum + slice.total,
    0,
  );

  return (
    <AppDrawer
      drawerWidth="11rem"
      hoverColor={palette.drawer.hoveredItem}
      logoAlign="start"
      headerDivider={false}
      logo={
        <Typography
          component="span"
          sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.35, pt: 1 }}
        >
          {t("app.name")}
        </Typography>
      }
      appBar={{
        kind: "titled",
        title: (
          <Typography noWrap sx={{ fontWeight: 600 }}>
            {isPhone && current !== undefined
              ? t(current.label)
              : t("app.name")}
          </Typography>
        ),
        end:
          sessionContext === null ? null : (
            // A long role wraps rather than cutting off itself or the title.
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.5px",
                lineHeight: 1.3,
                textTransform: "uppercase",
                textAlign: "end",
                maxWidth: "40%",
              }}
            >
              {sessionContext.roles
                .map((role) => t(`roles.${role}`))
                .join(" · ")}
            </Typography>
          ),
      }}
      drawerItems={<SessionContextNavigation waitingTotal={waitingTotal} />}
    >
      <Box
        sx={{
          flexGrow: 1,
          // Without this a wide child stretches the flex item rather than
          // scrolling inside it, and the drawer is pushed off the screen.
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          // Pinned to the viewport only beside the permanent drawer: under the
          // app bar the same height would overrun the page by the bar's own.
          minHeight: { md: "100vh" },
        }}
      >
        {/* A string with nowhere to break — an email address — otherwise sets
            its own minimum width and widens the document, leaving the drawer
            and app bar painted short of the page. Mid-word breaks are the
            lesser evil. */}
        <Box
          component="main"
          sx={{ flexGrow: 1, p: 3, minWidth: 0, overflowWrap: "anywhere" }}
        >
          <WaitingWorkContext.Provider value={waitingWork}>
            <Outlet />
          </WaitingWorkContext.Provider>
        </Box>

        {/* The first thing support asks for. A differing API version shows
            too: where `checkVersionMismatch` withholds the automatic reload,
            this is the only sign a newer build is waiting. */}
        <Box
          component="footer"
          sx={{ px: 3, py: 1, display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <Typography variant="caption" color="text.secondary">
            {t("version.client", { version: config.version })}
          </Typography>
          {serverVersion !== undefined && serverVersion !== config.version ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <Warning fontSize="inherit" titleAccess={t("version.mismatch")} />
              {t("version.server", { version: serverVersion })}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </AppDrawer>
  );
};
